import type { OneNoteNotebook, OneNoteSection } from '@api/lib/api-types';
import { api, ApiError } from '../api';

export interface PickerSelection {
  notebook: OneNoteNotebook;
  section: OneNoteSection;
}

export interface PickerCallbacks {
  uploadId: string;
  filename: string;
  onConfirmed: (importId: string, picked: PickerSelection) => void;
  onError: (err: ApiError | Error) => void;
}

export function renderNotebookPicker(host: HTMLElement, cb: PickerCallbacks): void {
  const card = document.createElement('section');
  card.className = 'card';

  const h2 = document.createElement('h2');
  h2.textContent = 'Pick a section in OneNote';

  const sub = document.createElement('p');
  sub.className = 'small muted';
  sub.textContent = `Your notes from ${cb.filename} will be imported into the section you pick.`;

  const refresh = document.createElement('button');
  refresh.type = 'button';
  refresh.className = 'btn btn-secondary';
  refresh.textContent = 'Refresh notebooks';

  const list = document.createElement('div');
  list.className = 'notebook-list';
  list.setAttribute('role', 'group');
  list.setAttribute('aria-label', 'OneNote notebooks and sections');

  const startBtn = document.createElement('button');
  startBtn.type = 'button';
  startBtn.className = 'btn btn-primary';
  startBtn.textContent = 'Start import';
  startBtn.disabled = true;

  let picked: PickerSelection | null = null;

  function paintList(notebooks: OneNoteNotebook[]) {
    list.replaceChildren();
    if (notebooks.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'small muted';
      empty.textContent = "We didn't find any OneNote notebooks in your account. Create one in OneNote, then click Refresh.";
      list.append(empty);
      return;
    }
    for (const nb of notebooks) {
      const nbEl = document.createElement('details');
      nbEl.className = 'notebook';
      nbEl.open = notebooks.length === 1;

      const summary = document.createElement('summary');
      summary.textContent = nb.name;
      nbEl.append(summary);

      if (nb.sections.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'small muted';
        empty.textContent = 'No sections in this notebook.';
        nbEl.append(empty);
      } else {
        for (const sec of nb.sections) {
          const radioId = `sec-${nb.id}-${sec.id}`;
          const label = document.createElement('label');
          label.className = 'section-row';
          label.setAttribute('for', radioId);

          const radio = document.createElement('input');
          radio.type = 'radio';
          radio.name = 'section';
          radio.id = radioId;
          radio.value = sec.id;
          radio.addEventListener('change', () => {
            picked = { notebook: nb, section: sec };
            startBtn.disabled = false;
          });

          const span = document.createElement('span');
          span.textContent = sec.name;

          label.append(radio, span);
          nbEl.append(label);
        }
      }
      list.append(nbEl);
    }
  }

  async function load() {
    refresh.disabled = true;
    list.replaceChildren(spinner('Loading notebooks…'));
    try {
      const res = await api.notebooks();
      paintList(res.notebooks ?? []);
    } catch (err) {
      cb.onError(err as Error);
    } finally {
      refresh.disabled = false;
    }
  }

  refresh.addEventListener('click', load);

  startBtn.addEventListener('click', async () => {
    if (!picked) return;
    startBtn.disabled = true;
    startBtn.textContent = 'Starting…';
    try {
      const { importId } = await api.createImport({
        uploadId: cb.uploadId,
        sectionId: picked.section.id,
      });
      cb.onConfirmed(importId, picked);
    } catch (err) {
      startBtn.disabled = false;
      startBtn.textContent = 'Start import';
      cb.onError(err as Error);
    }
  });

  card.append(h2, sub, refresh, list, startBtn);
  host.replaceChildren(card);
  void load();
}

function spinner(text: string): HTMLElement {
  const p = document.createElement('p');
  p.className = 'small muted';
  p.innerHTML = `<span class="spinner" aria-hidden="true"></span> `;
  p.appendChild(document.createTextNode(text));
  return p;
}
